/**
 * Invoice / self-bill / credit-note totals — the single source of truth.
 *
 * Call `getInvoiceTotals(lines)` for a normal invoice.
 * Call `getInvoiceTotals(lines, cashDiscountPercentage)` when a cash discount
 * applies; the caller decides that (today: PC7 + a percentage > 0).
 *
 * Units
 * -----
 * - Money: integer cents (€10.00 = 1000).
 * - Quantity (`amount`): hundredths (1.00 = 100).
 * - VAT rate (`vatRatePercentage`): basis points (21% = 2100, 6% = 600).
 * - Cash-discount percentage: percent (2.5 = 2.5%).
 *
 * Belgian VAT (art. 28, 1° W.BTW)
 * --------------------------------
 * A cash discount is deducted from the excl. base *before* VAT. The VAT on
 * the document is therefore always VAT on the discounted base.
 *
 * Two payable totals (TRA-194)
 * ----------------------------
 * - With discount: discounted base + VAT on that base.
 * - Without discount: full excl. + that same VAT, since the VAT stays on the
 *   reduced base whether or not the discount is taken, e.g.
 *   €2 400 − 2,5% → €2 340 + €491,40 VAT; without discount €2 400 + €491,40 = €2 891,40.
 */

/** Marker on the grand-total row (credit-note / export consumers). */
export const INVOICE_TOTALS_VAT_RATE_ID = 'T';

export interface ILineAmount {
  amount: number;
  unitPrice: number;
  vatRatePercentage?: number;
  vatRateId?: string;
}

export interface IInvoiceLine {
  amount: number;
  unitPrice: number;
  vatRatePercentage: number;
  vatRateId?: string;
}

export interface ITotalAmounts {
  vatRateId: string;
  vatRatePercentage: number;
  totalExclVat: number;
  totalVat: number;
  totalInclVat: number;
}

export interface ICashDiscountVatRate {
  vatRateId: string;
  vatRatePercentage: number;
  discountAmount: number;
  taxableBase: number;
  totalVat: number;
}

export interface ICashDiscount {
  percentage: number;
  discountAmount: number;
  taxableBase: number;
  /** VAT on the discounted base — the VAT reported on the document. */
  totalVat: number;
  perVatRate: ICashDiscountVatRate[];
  /** Discounted base + VAT on that base. */
  totalWithDiscount: number;
  /** Full excl. + VAT on the discounted base (TRA-194). */
  totalWithoutDiscount: number;
}

export interface IInvoiceTotals {
  vatRateId: typeof INVOICE_TOTALS_VAT_RATE_ID;
  perVatRate: Record<number, ITotalAmounts>;

  /** Undiscounted excl. VAT. */
  totalExclVat: number;
  /** Undiscounted VAT (normale regel). */
  totalVat: number;
  /** Full excl. + VAT on the full excl. — without a cash discount, the invoice total. */
  totalInclVat: number;

  /** VAT taxable base: after cash discount, otherwise `totalExclVat`. */
  taxableBase: number;
  /** VAT on `taxableBase` — what the document reports. */
  payableVat: number;
  /** Amount booked / due with the cash discount, otherwise `totalInclVat`. */
  totalPayable: number;

  /** Set when a positive cash-discount percentage was passed in. */
  cashDiscount?: ICashDiscount;
}

/** Line total excl. VAT in cents. Quantity is in hundredths, unit price in cents. */
export function calculateLineTotal(line: ILineAmount): number {
  return (line.amount * line.unitPrice) / 100;
}

export function calculateLinesTotalExclVat(lines: ILineAmount[]): number {
  return lines.reduce((acc, line) => acc + calculateLineTotal(line), 0);
}

export function calculateLinesTotalVat(lines: IInvoiceLine[]): number {
  return getInvoiceTotals(lines).totalVat;
}

function roundToCents(value: number): number {
  return Math.round(value);
}

function vatOnExcl(exclVatCents: number, vatRatePercentage: number): number {
  return roundToCents((exclVatCents * vatRatePercentage) / 10000);
}

function sum(values: number[]): number {
  return values.reduce((acc, value) => acc + value, 0);
}

/**
 * Totals for an invoice, self-bill, or credit note.
 *
 * Pass `cashDiscountPercentage` only when a discount should be applied.
 * A missing, zero, or negative percentage is treated as no discount.
 */
export function getInvoiceTotals(lines: IInvoiceLine[], cashDiscountPercentage?: number): IInvoiceTotals {
  const perVatRate: Record<number, ITotalAmounts> = {};

  for (const line of lines) {
    if (!perVatRate[line.vatRatePercentage]) {
      perVatRate[line.vatRatePercentage] = {
        vatRateId: line.vatRateId ?? '',
        vatRatePercentage: line.vatRatePercentage,
        totalExclVat: 0,
        totalVat: 0,
        totalInclVat: 0,
      };
    }
    perVatRate[line.vatRatePercentage].totalExclVat += calculateLineTotal(line);
  }

  // Normal rule: VAT on the full excl. amount, rounded per rate so the
  // grand totals are the sum of the per-rate rows.
  for (const rate of Object.values(perVatRate)) {
    rate.totalExclVat = roundToCents(rate.totalExclVat);
    rate.totalVat = vatOnExcl(rate.totalExclVat, rate.vatRatePercentage);
    rate.totalInclVat = rate.totalExclVat + rate.totalVat;
  }

  const rates = Object.values(perVatRate);
  const totalExclVat = sum(rates.map((rate) => rate.totalExclVat));
  const totalVat = sum(rates.map((rate) => rate.totalVat));
  const totalInclVat = sum(rates.map((rate) => rate.totalInclVat));

  if (cashDiscountPercentage == null || cashDiscountPercentage <= 0) {
    return {
      vatRateId: INVOICE_TOTALS_VAT_RATE_ID,
      perVatRate,
      totalExclVat,
      totalVat,
      totalInclVat,
      taxableBase: totalExclVat,
      payableVat: totalVat,
      totalPayable: totalInclVat,
    };
  }

  // Art. 28 W.BTW: take the discount off each VAT rate, then calculate VAT
  // on that reduced base. Sum the per-rate rows so rounding still adds up.
  const discountPerRate: ICashDiscountVatRate[] = rates.map((rate) => {
    const discountAmount = roundToCents((rate.totalExclVat * cashDiscountPercentage) / 100);
    const taxableBase = rate.totalExclVat - discountAmount;
    return {
      vatRateId: rate.vatRateId,
      vatRatePercentage: rate.vatRatePercentage,
      discountAmount,
      taxableBase,
      totalVat: vatOnExcl(taxableBase, rate.vatRatePercentage),
    };
  });

  const taxableBase = sum(discountPerRate.map((rate) => rate.taxableBase));
  const payableVat = sum(discountPerRate.map((rate) => rate.totalVat));
  const discountAmount = totalExclVat - taxableBase;
  const totalWithDiscount = taxableBase + payableVat;

  return {
    vatRateId: INVOICE_TOTALS_VAT_RATE_ID,
    perVatRate,
    totalExclVat,
    totalVat,
    totalInclVat,
    taxableBase,
    payableVat,
    totalPayable: totalWithDiscount,
    cashDiscount: {
      percentage: cashDiscountPercentage,
      discountAmount,
      taxableBase,
      totalVat: payableVat,
      perVatRate: discountPerRate,
      totalWithDiscount,
      totalWithoutDiscount: totalExclVat + payableVat,
    },
  };
}
